import dayjs, { type Dayjs } from "dayjs";

import { type PayoffGrantOfferRequest, type PayoffGrantedOffer } from "@fitness-app/data-models/entities/Payoff";

export const DEFAULT_DISCOUNT_PCT = 10;
export const DEFAULT_OFFER_DAYS = 14;
export const DEFAULT_FOLLOW_UP_DAYS = 3;
/** Business cap, not a platform one — see MAX_FOLLOW_UP_DAYS in GrantPayoffOfferUseCase. */
export const MIN_FOLLOW_UP_DAYS = 1;
export const MAX_FOLLOW_UP_DAYS = 30;
/** The bounds themselves live in data-models — one source for form and server. */
export const DEFAULT_BONUS_DAYS = 30;
export { MAX_BONUS_DAYS, MIN_BONUS_DAYS } from "@fitness-app/data-models/entities/Payoff";

export type PayoffRewardType = "discount" | "lastInstallmentFree" | "bonusTime";

/** Campaign part of both grant forms — single client and bulk (TRE-1721). */
export interface PayoffOfferCampaignModel {
  rewardType: PayoffRewardType;
  discountPct: number;
  /** Extra access days of the `bonusTime` reward (TRE-1725). */
  bonusDays: number;
  expiresAt: Dayjs;
  sendEmail: boolean;
  followUpEnabled: boolean;
  followUpAfterDays: number;
}

/**
 * One place that turns either form into the grant payload.
 *
 * Both modals send to the same endpoint with the same rules, and the condition
 * that is easy to get wrong — a reminder needs the offer email — must not be
 * enforced in one modal and forgotten in the other. The backend re-validates
 * all of it anyway (PayoffOfferControllers).
 */
const buildReward = (model: PayoffOfferCampaignModel): PayoffGrantOfferRequest["reward"] => {
  if (model.rewardType === "discount") {
    return { type: "discount", discountPct: model.discountPct };
  }

  if (model.rewardType === "bonusTime") {
    return { type: "bonusTime", bonusDays: model.bonusDays };
  }

  return { type: "lastInstallmentFree" };
};

export const buildGrantOfferPayload = (
  model: PayoffOfferCampaignModel,
  productClientIds: string[],
): PayoffGrantOfferRequest => ({
  productClientIds,
  // Still sent for the discount reward so callers and logs keep the number
  // where they have always read it.
  discountPct: model.rewardType === "discount" ? model.discountPct : 0,
  reward: buildReward(model),
  expiresAt: model.expiresAt.endOf("day").valueOf(),
  sendEmail: model.sendEmail,
  followUp: model.sendEmail && model.followUpEnabled ? { enabled: true, afterDays: model.followUpAfterDays } : null,
});

/**
 * A reminder due after the offer is gone would arrive as an invitation to
 * something that no longer exists — the server rejects it, so catch it in the
 * form while the operator can still move either date.
 */
export const followUpFitsInOffer = (afterDays: number, expiresAt: Dayjs | null | undefined): boolean =>
  Boolean(expiresAt) && (expiresAt as Dayjs).endOf("day").valueOf() > Date.now() + afterDays * 24 * 3600 * 1000;

/**
 * Is the offer still something the client can take?
 *
 * Used by BOTH the client row and the client details, because they disagreed
 * after TRE-1737: a payoff that was already paid left the gold "offer until …"
 * tag hanging on a settled plan. Redeemed, revoked and expired all mean the
 * same thing here — there is nothing left to take.
 */
export const isOfferOpen = (
  offer: Pick<PayoffGrantedOffer, "enabled" | "revokedAt" | "expiresAt" | "redeemedAt"> | null | undefined,
  now = Date.now(),
): boolean => Boolean(offer?.enabled && !offer.revokedAt && !offer.redeemedAt && offer.expiresAt > now);

/** How the offer reads on a client row / tag. */
export const rewardLabel = (offer: Pick<PayoffGrantedOffer, "discountPct" | "reward">): string => {
  const reward = offer.reward;

  if (reward?.type === "lastInstallmentFree") {
    return "ostatnia rata gratis";
  }

  if (reward?.type === "bonusTime") {
    return `+${reward.bonusDays} dni dostępu`;
  }

  return `−${reward?.type === "discount" ? reward.discountPct : offer.discountPct}%`;
};

const FOLLOW_UP_SKIP_LABELS: Record<string, string> = {
  converted: "klientka dopłaciła wcześniej",
  expired: "oferta wygasła",
  revoked: "oferta cofnięta",
  not_eligible: "plan przestał się kwalifikować",
  disabled: "wyłączone przy przyznawaniu",
};

/**
 * Whether and when the reminder went out — the answer the trainer looks for on
 * a client who says she never got a second message.
 */
export const followUpLabel = (followUp: PayoffGrantedOffer["followUp"]): string => {
  if (!followUp) {
    return "Bez przypomnienia";
  }

  if (followUp.sentAt) {
    return `Przypomnienie wysłane ${dayjs(followUp.sentAt).format("DD.MM.YYYY HH:mm")}`;
  }

  if (followUp.skippedReason) {
    return `Przypomnienie pominięte — ${FOLLOW_UP_SKIP_LABELS[followUp.skippedReason] ?? followUp.skippedReason}`;
  }

  return `Przypomnienie zaplanowane na ${dayjs(followUp.scheduledAt).format("DD.MM.YYYY")}`;
};
